import { Injectable, NgZone } from '@angular/core';
import { CommonHttpService } from './http.service';
import { Subscription } from 'rxjs/Subscription';
import { StoreConfigurationsService } from './store-configurations.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable()
export class GoogleAnalyticsService {
    protected gaAccount: string = null;

    protected dataLoaderSubscription: Subscription;
    protected trackPageQueue = [];

    constructor(
        protected http: CommonHttpService,
        protected storeConfigurations: StoreConfigurationsService,
        protected router: Router,
        protected zone: NgZone
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.trackPageNewGa(event.urlAfterRedirects, 'G-29TE1B8ZRK');

                if (this.canTrackPage()) {
                    this.trackPageDeprecatedGa(event.urlAfterRedirects);
                    this.trackPageNewGa(event.urlAfterRedirects);
                }
                this.trackPageQueue.push(event.urlAfterRedirects);
            }
        });
        this.dataLoaderSubscription = this.storeConfigurations
            .dataLoaded$
            .subscribe((configurations) => {
                this.runForStoreData(configurations['google-analytics-account']);
            });
        if (this.dataLoaderSubscription.closed) {
            this.runForStoreData(this.storeConfigurations.get('google-analytics-account'));
        }
    }

    /**
     * Call internal queue processor.
     **/
    private runForStoreData(gaAccount = null) {
        if (gaAccount !== null) {
            this.gaAccount = gaAccount;
        }

        /**
         * When page url track is faster then configuration request.
         * All tracks should be queued and process by commands below.
         **/
        this.dataLoaderSubscription.unsubscribe();
        this.processQueues();
    }

    /**
     * Send pageview event to Google Analytics.
     **/
    public trackPageDeprecatedGa(url: string = null, accountPrefix: string = '') {
        try {
            this.zone.run(() => {
                if (window['ga'] == undefined) {
                    console.warn('GA library is not defined');
                    return;
                }
                if (this.gaAccount !== null) {
                    window['ga']('create', this.gaAccount, 'auto');
                }
                if (url !== null) {
                    window['ga'](`${accountPrefix}set`, 'page', url);
                }

                window['ga'](`${accountPrefix}send`, 'pageview');
            });
        } catch (e) {
            console.warn(e);
        }
    }

    public trackPageNewGa(url: string, sendTo: string = '') {
        try {
            this.zone.run(() => {
                if (window['gtag'] == undefined) {
                    console.warn('GA4 (gtag) library is not defined');
                    return;
                }
                if (this.gaAccount !== null) {
                    window['gtag']('config', sendTo);
                }
                if (url !== null) {
                    window['gtag']('event', 'page_view', {'sent_to': sendTo, 'page_location': url});
                }
            });
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Process all events queues.
     **/
    private processQueues() {
        if (this.trackPageQueue.length > 0) {
            this.trackPageQueue.forEach(data => this.trackPageDeprecatedGa(data));
            this.trackPageQueue.forEach(data => this.trackPageNewGa(data, this.gaAccount));
            this.trackPageQueue = [];
        }
    }

    /**
     * Check if ga script is imported and ga account is defined.
     **/
    private canTrackPage(): boolean {
        return this.isWindowGAScriptDefined()
            && this.isGAAccountDefined()
            && this.dataLoaderSubscription.closed;
    }

    /**
     * Check if GA script was included to page.
     **/
    private isWindowGAScriptDefined() {
        return (window['ga'] !== undefined && window['ga'] !== null);
    }

    /**
     * Check if gaAccount id is defined.
     **/
    private isGAAccountDefined() {
        return (this.gaAccount !== undefined && this.gaAccount !== null);
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src4078178155/src/src/app/shared/services/google-analytics.service.ts